import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SERVICES, SERVICE_GROUPS, type RequestUrgency } from '@shared';
import { apiPost, ApiError } from '../../lib/api';

interface State {
  service: string;
  urgency: RequestUrgency;
  headline: string;
  description: string;
  counterparty: string;
  body_corporate_confirmed: boolean;
  sophisticated_self_cert: boolean;
  not_advice_acknowledged: boolean;
  kyc_acknowledged: boolean;
}

const INITIAL: State = {
  service: 'sblc',
  urgency: 'standard',
  headline: '',
  description: '',
  counterparty: '',
  body_corporate_confirmed: false,
  sophisticated_self_cert: false,
  not_advice_acknowledged: false,
  kyc_acknowledged: false,
};

export function NewRequest() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselectedService = params.get('service');
  const initialState: State = preselectedService && SERVICES.some((s) => s.id === preselectedService)
    ? { ...INITIAL, service: preselectedService }
    : INITIAL;
  const [step, setStep] = useState(1);
  const [d, setD] = useState<State>(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof State>(k: K) => (e: { target: { value: string } }) =>
    setD((s) => ({ ...s, [k]: e.target.value as State[K] }));
  const toggle = (k: 'body_corporate_confirmed' | 'sophisticated_self_cert' | 'not_advice_acknowledged' | 'kyc_acknowledged') => () =>
    setD((s) => ({ ...s, [k]: !s[k] }));

  const svc = SERVICES.find((s) => s.id === d.service);

  const stepReady =
    step === 1 ? d.body_corporate_confirmed && d.sophisticated_self_cert && d.not_advice_acknowledged && d.kyc_acknowledged :
    step === 2 ? d.headline.length > 0 && d.description.length >= 20 :
    true;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        service: d.service,
        urgency: d.urgency,
        headline: d.headline,
        description: d.description,
        counterparty: d.counterparty || undefined,
        body_corporate_confirmed: d.body_corporate_confirmed,
        sophisticated_self_cert: d.sophisticated_self_cert,
        not_advice_acknowledged: d.not_advice_acknowledged,
        kyc_acknowledged: d.kyc_acknowledged,
      };
      const r = await apiPost<{ request: { id: string } }>('/api/requests', payload);
      navigate(`/portal/requests/${r.request.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <h1 className="portal-h1">New brief</h1>
      <p className="portal-sub">
        Step {step} of 3 — {step === 1 ? 'Eligibility' : step === 2 ? 'Engagement scope' : 'Review & submit'}
      </p>

      <div className="statusbar" style={{ marginTop: 24, gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {['Eligibility', 'Scope', 'Review'].map((s, i) => (
          <div key={s} className={`step ${step - 1 > i ? 'done' : step - 1 === i ? 'now' : ''}`}>
            {String(i + 1).padStart(2, '0')} {s}
          </div>
        ))}
      </div>

      <div className="card" style={{ marginTop: 24, padding: 'clamp(24px, 4vw, 40px)' }}>
        {step === 1 && (() => {
          const allFour =
            d.body_corporate_confirmed &&
            d.sophisticated_self_cert &&
            d.not_advice_acknowledged &&
            d.kyc_acknowledged;
          const toggleAll = () =>
            setD((s) => {
              const next = !(
                s.body_corporate_confirmed &&
                s.sophisticated_self_cert &&
                s.not_advice_acknowledged &&
                s.kyc_acknowledged
              );
              return {
                ...s,
                body_corporate_confirmed: next,
                sophisticated_self_cert: next,
                not_advice_acknowledged: next,
                kyc_acknowledged: next,
              };
            });
          return (
            <>
              <h3 style={{ fontSize: 19 }}>Eligibility gate</h3>
              <p style={{ color: 'var(--fg-soft)', fontSize: 13.5, marginTop: 6, maxWidth: '60ch' }}>
                Omega only accepts briefs from sophisticated body corporates. By selecting this
                checkbox, you confirm that all eligibility requirements listed below have been
                reviewed and satisfied.
              </p>
              <ul style={{
                margin: '24px 0 0',
                paddingLeft: 22,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                fontSize: 13.5,
                color: 'var(--fg-soft)',
                lineHeight: 1.55,
              }}>
                <li>
                  <strong style={{ color: 'var(--fg)' }}>Body corporate.</strong>{' '}
                  We are a Limited Company, PLC or LLP — not a sole trader, partnership of fewer than 4, or individual.
                </li>
                <li>
                  <strong style={{ color: 'var(--fg)' }}>Sophisticated / HNW corporate.</strong>{' '}
                  Our company qualifies under the FPO "investment professional" or "high net worth body corporate" exemptions.
                </li>
                <li>
                  <strong style={{ color: 'var(--fg)' }}>Not investment advice.</strong>{' '}
                  We acknowledge Omega Trade and Finance Ltd is not authorised or regulated by the FCA and provides administrative, document-preparation and introducer services only — not investment advice or recommendations.
                </li>
                <li>
                  <strong style={{ color: 'var(--fg)' }}>KYC / AML.</strong>{' '}
                  We will provide beneficial-ownership documentation and source-of-funds where required for any introduction.
                </li>
              </ul>
              <label
                className={`checkbox ${allFour ? 'on' : ''}`}
                onClick={toggleAll}
                style={{ marginTop: 18 }}
              >
                <span className="box" />
                <span>I confirm all the eligibility criteria are met.</span>
              </label>
            </>
          );
        })()}

        {step === 2 && (
          <>
            <h3 style={{ fontSize: 19 }}>Engagement scope</h3>
            <p style={{ color: 'var(--fg-soft)', fontSize: 13.5, marginTop: 6, maxWidth: '60ch' }}>
              Tell us what you need. We’ll respond with a written engagement letter within two business days.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 24 }}>
              <div className="field">
                <label>Service line</label>
                <select value={d.service} onChange={set('service')}>
                  {SERVICE_GROUPS.map((g) => (
                    <optgroup key={g.id} label={g.label}>
                      {SERVICES.filter((s) => s.group === g.id).map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <div className="hint">{svc?.summary}</div>
              </div>
              <div className="field">
                <label>Urgency</label>
                <select value={d.urgency} onChange={set('urgency')}>
                  <option value="standard">Standard ({svc?.lead})</option>
                  <option value="expedited">Expedited (+25% fee)</option>
                  <option value="exploratory">Exploratory — no deadline</option>
                </select>
              </div>
            </div>
            <div className="field" style={{ marginTop: 14 }}>
              <label>Engagement headline<span className="req">*</span></label>
              <input
                required
                value={d.headline}
                onChange={set('headline')}
                placeholder="e.g. SBLC for marine bunker offtake — $24m, 12 months"
                aria-required="true"
                aria-invalid={d.headline.length === 0 ? true : undefined}
              />
            </div>
            <div className="field" style={{ marginTop: 14 }}>
              <label>Description<span className="req">*</span></label>
              <textarea
                required
                minLength={20}
                value={d.description}
                onChange={set('description')}
                rows={5}
                placeholder="Tell us about the corporate, the transaction or research need, and the timing."
                aria-required="true"
                aria-invalid={d.description.length < 20 ? true : undefined}
              />
              <div className="hint" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>
                  Do not include sensitive personal data.{' '}
                  <Link to="/portal/compliance" style={{ color: 'var(--accent)' }}>
                    KYC documentation is collected in your Compliance page →
                  </Link>
                </span>
                <span style={{
                  fontFamily: 'var(--mono)',
                  color: d.description.length < 20 ? 'var(--red, #c0392b)' : 'var(--fg-mute)',
                  whiteSpace: 'nowrap',
                }}>
                  {d.description.length} / 20 min
                </span>
              </div>
            </div>
            {step === 2 && !stepReady && (d.headline.length > 0 || d.description.length > 0) && (
              <div style={{
                marginTop: 14, padding: '10px 14px',
                background: 'color-mix(in oklab, var(--red, #c0392b) 8%, transparent)',
                color: 'var(--red, #c0392b)',
                borderRadius: 'var(--r-sm)', fontSize: 12.5, lineHeight: 1.5,
              }}>
                {d.headline.length === 0
                  ? 'Engagement headline is required.'
                  : `Description must be at least 20 characters (currently ${d.description.length}).`}
              </div>
            )}
            <div className="field" style={{ marginTop: 14 }}>
              <label>Counterparty (if known)</label>
              <input
                value={d.counterparty}
                onChange={set('counterparty')}
                placeholder="Optional — name of bank, fund or counterparty if already identified"
              />
            </div>
            <div style={{ marginTop: 24, padding: 16, borderRadius: 'var(--r)', background: 'var(--accent-soft)', fontSize: 12.5, color: 'var(--fg-soft)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--fg)' }}>Reminder.</strong> {svc?.not_regulated}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h3 style={{ fontSize: 19 }}>Review & submit</h3>
            <p style={{ color: 'var(--fg-soft)', fontSize: 13.5, marginTop: 6, maxWidth: '60ch' }}>
              Review the brief below. On submit, you’ll receive a reference number and an Omega
              engagement lead will respond within two UK business days.
            </p>
            <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, border: '1px solid var(--rule)', borderRadius: 'var(--r)' }}>
              {[
                ['Service', svc?.name],
                ['Urgency', d.urgency],
                ['Headline', d.headline || '—'],
                ['Counterparty', d.counterparty || 'Not specified'],
                ['Indicative fee', svc?.fee],
                ['Indicative lead time', svc?.lead],
              ].map(([k, v], i) => (
                <div
                  key={String(k)}
                  style={{
                    padding: 14,
                    borderTop: i > 1 ? '1px solid var(--rule)' : 0,
                    borderRight: i % 2 === 0 ? '1px solid var(--rule)' : 0,
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', letterSpacing: 0.06, textTransform: 'uppercase', marginBottom: 4 }}>{k}</div>
                  <div>{v}</div>
                </div>
              ))}
              <div style={{ padding: 14, borderTop: '1px solid var(--rule)', gridColumn: '1 / -1', fontSize: 13 }}>
                <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', letterSpacing: 0.06, textTransform: 'uppercase', marginBottom: 4 }}>Description</div>
                <div style={{ color: 'var(--fg-soft)', whiteSpace: 'pre-wrap' }}>{d.description}</div>
              </div>
            </div>
          </>
        )}

        {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 16 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, gap: 16 }}>
          {step === 1 ? (
            // No "back" inside the wizard at this point — give the user a clean
            // way out of the flow instead of an inert button.
            <Link
              to="/portal"
              style={{
                fontSize: 13,
                color: 'var(--fg-soft)',
                display: 'inline-flex',
                gap: 4,
                textDecoration: 'none',
              }}
            >
              ← Return to dashboard
            </Link>
          ) : (
            <button className="btn ghost" type="button" onClick={() => setStep((s) => s - 1)}>← Back</button>
          )}
          {step < 3 ? (
            <button className="btn accent" type="button" disabled={!stepReady} onClick={() => setStep((s) => s + 1)}>Continue →</button>
          ) : (
            <button className="btn accent" type="button" disabled={submitting} onClick={submit}>
              {submitting ? 'Submitting…' : 'Submit brief →'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
